import type {
  DataInputTransformer,
  UserFileTransformer,
} from "@/types/workspace/Transformer";
import { logger } from "@/utils/debug";
import { Europafacts } from "../api/europafacts";
import { indexedDBService } from "../storage/indexedDB";

class SourceDataLoader {
  /**
   * Loads source data from UserFile transformer
   * @param transformer - The UserFile transformer to load the data from
   * @returns A promise that resolves with the source data
   */
  async loadUserFileData(transformer: UserFileTransformer): Promise<any[]> {
    const fileData = await indexedDBService.getFileData(transformer.file_id);
    if (!fileData) {
      throw new Error(`File not found: ${transformer.file_id}`);
    }
    return fileData.data;
  }

  /**
   * Loads source data from DataInput transformer (bookmarked dataset)
   * Checks IndexedDB cache first, then fetches from API if not cached
   * @param transformer - The DataInput transformer to load the data from
   * @returns A promise that resolves with the source data
   */
  async loadDatasetData(transformer: DataInputTransformer): Promise<any[]> {
    const dbTableId = transformer.db_table_id;
    if (!dbTableId) {
      throw new Error("Dataset db_table_id is required");
    }

    const cachedData = await indexedDBService.getDatasetData(dbTableId);
    if (cachedData) {
      logger.log(
        `[SourceDataLoader] Using cached data for dataset ${dbTableId}`
      );
      return cachedData.data;
    }

    logger.log(
      `[SourceDataLoader] Fetching dataset ${dbTableId} from API...`
    );
    const data = await Europafacts.getDatasetData(dbTableId);

    if (!data || data.length === 0) {
      throw new Error(`No data found for dataset ${dbTableId}`);
    }

    let metadata;
    try {
      const datasetMetadata = await Europafacts.getMetadataForDataset(
        dbTableId
      );
      const columns = this.extractColumns(data);
      metadata = {
        fileId: dbTableId,
        fileName:
          datasetMetadata.table_name_english || datasetMetadata.table_name,
        fileType: "application/json",
        fileSize: JSON.stringify(data).length,
        rowCount: datasetMetadata.row_count || data.length,
        columnCount: datasetMetadata.column_count || columns.length,
        columns: columns,
        createdAt: datasetMetadata.datetime_created || new Date().toISOString(),
      };
    } catch (error) {
      logger.warn(
        `[SourceDataLoader] Failed to fetch metadata for ${dbTableId}, using data-derived metadata`,
        error
      );
      const columns = this.extractColumns(data);
      metadata = {
        fileId: dbTableId,
        fileName: `Dataset ${dbTableId}`,
        fileType: "application/json",
        fileSize: JSON.stringify(data).length,
        rowCount: data.length,
        columnCount: columns.length,
        columns: columns,
        createdAt: new Date().toISOString(),
      };
    }

    await indexedDBService.storeDatasetData(dbTableId, data, metadata);

    return data;
  }

  /**
   * Extracts the columns from the data
   * @param data - The data to extract the columns from
   * @returns The columns
   */
  private extractColumns(data: any[]): string[] {
    if (data.length === 0) return [];
    return Object.keys(data[0]);
  }
}

export const sourceDataLoader = new SourceDataLoader();

